import React, { useCallback, useEffect, useRef, useState } from 'react';
import { audioConstraints, isGone, listDevices, remembered, resolve } from '../../lib/devices';
import { toWav16k } from '../../lib/voiceWav';
import { rememberLanguage } from '../../lib/voiceDraft';
import { Icon } from './icons';
import './TodoAssist.css';

/*
 * VOICE TO-DOS (to-do Phase 2, batch C).
 *
 * Gordon: a mic in quick add. Press it (or hold it, on a phone, and let go),
 * say "ring Rita tomorrow at three about the VAT return", and see the to-do
 * it makes - never made by itself: a preview card, editable, with what was
 * heard underneath, and Create / Edit / Cancel.
 *
 * Three pieces, used by TodoApp's quick add and by chat's Make a to-do:
 *
 *   useDictation   the microphone - the one chosen in the call device picker
 *                  (lib/devices.js) when there is one - a MediaRecorder, a
 *                  level meter, a 30-second cap; then the recording made
 *                  into a 16 kHz WAV (lib/voiceWav.js) and sent to
 *                  POST /todos/voice for its text. The audio is not kept,
 *                  here or on the server.
 *   MicButton      press to start, press Stop to finish; held down for more
 *                  than half a second, letting go finishes it.
 *   VoicePanel     what quick add shows meanwhile: a red dot, the level,
 *                  the time against the cap, the language chip, Stop and
 *                  Cancel. Esc cancels; Space or Enter stops.
 *   VoiceDraftList the preview, in VoiceDrafts.js: every to-do the sentence
 *                  makes (one sentence can be several - 23 October).
 *
 * Whether any of it is drawn is lib/todoAssistGate.js's question, not this
 * file's.
 */

const TYPES = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg'];
const pickType = () => {
  const MR = window.MediaRecorder;
  if (!MR || typeof MR.isTypeSupported !== 'function') return '';
  return TYPES.find((t) => { try { return MR.isTypeSupported(t); } catch { return false; } }) || '';
};

export const clock = (s) => `${Math.floor(Math.max(0, s) / 60)}:${String(Math.floor(Math.max(0, s) % 60)).padStart(2, '0')}`;

/** Said when the audio goes out, wherever the mic is offered. */
export const PRIVACY = 'Your voice is sent to Azure Speech to be turned into text, and is not kept.';

const BARS = 18;

/**
 * The microphone, start to text.
 *
 *   phase  idle | asking | recording | converting | sending
 *   level  the last BARS loudness readings, 0..1, newest last
 *
 * `send(blob, language)` posts the WAV and answers the server's { text,
 * language }; `onText` gets them, `onError` gets a sentence.
 */
export function useDictation({ send, maxSeconds = 30, language, onText, onError }) {
  const [phase, setPhase] = useState('idle');
  const [elapsed, setElapsed] = useState(0);
  const [level, setLevel] = useState(() => new Array(BARS).fill(0));
  const [missing, setMissing] = useState(null);   // the chosen mic, when it is not plugged in
  const stream = useRef(null);
  const recorder = useRef(null);
  const audio = useRef(null);
  const frame = useRef(null);
  const timer = useRef(null);
  const attempt = useRef(0);
  const began = useRef(0);
  const discard = useRef(false);
  const lang = useRef(language);
  lang.current = language;
  const cbs = useRef({ send, onText, onError });
  cbs.current = { send, onText, onError };

  const release = useCallback(() => {
    if (timer.current) { clearInterval(timer.current); timer.current = null; }
    if (frame.current) { cancelAnimationFrame(frame.current); frame.current = null; }
    if (audio.current) { try { audio.current.close(); } catch { /* closed */ } audio.current = null; }
    if (stream.current) {
      stream.current.getTracks().forEach((t) => { try { t.stop(); } catch { /* gone */ } });
      stream.current = null;
    }
  }, []);

  const cancel = useCallback(() => {
    attempt.current += 1;
    discard.current = true;
    const r = recorder.current;
    recorder.current = null;
    if (r && r.state !== 'inactive') { try { r.stop(); } catch { /* stopped */ } }
    release();
    setPhase('idle');
    setElapsed(0);
    setLevel(new Array(BARS).fill(0));
  }, [release]);

  const stop = useCallback(() => {
    const r = recorder.current;
    if (!r || r.state === 'inactive') return;
    discard.current = false;
    try { r.stop(); } catch { cancel(); }
  }, [cancel]);

  const say = useCallback((words) => { if (cbs.current.onError) cbs.current.onError(words); }, []);

  const start = useCallback(async () => {
    if (recorder.current || stream.current) return;
    const mine = ++attempt.current;
    setPhase('asking');
    setMissing(null);
    if (cbs.current.onError) cbs.current.onError(null);
    // The microphone chosen in the call device picker, if it is plugged in.
    let deviceId = null;
    try {
      const pick = resolve('audioinput', await listDevices(), remembered());
      deviceId = pick.deviceId;
      if (pick.missing) setMissing(pick.missing.label || 'Your chosen microphone');
    } catch { /* the system default, then */ }
    let media;
    let refused = null;
    try {
      media = await navigator.mediaDevices.getUserMedia({ audio: audioConstraints(deviceId) });
    } catch (err) {
      refused = err;
      if (deviceId && isGone(err)) {
        try { media = await navigator.mediaDevices.getUserMedia({ audio: audioConstraints(null) }); } catch (e2) { refused = e2; }
      }
    }
    if (!media) {
      if (mine !== attempt.current) return;
      setPhase('idle');
      const name = refused && refused.name;
      say(name === 'NotFoundError' || name === 'OverconstrainedError' ? 'No microphone was found. Plug one in, or type the to-do.'
        : name === 'NotReadableError' ? 'The microphone is in use by another program.'
          : 'Allow the microphone in your browser to dictate a to-do.');
      return;
    }
    if (mine !== attempt.current) { media.getTracks().forEach((t) => t.stop()); return; }
    stream.current = media;

    // The level meter: the loudness, a few times a second, newest on the right.
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      const ctx = new AC();
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      ctx.createMediaStreamSource(media).connect(analyser);
      audio.current = ctx;
      const data = new Uint8Array(analyser.fftSize);
      let lastAt = 0;
      const tick = (t) => {
        frame.current = requestAnimationFrame(tick);
        if (t - lastAt < 70) return;
        lastAt = t;
        analyser.getByteTimeDomainData(data);
        let sum = 0;
        for (let i = 0; i < data.length; i += 1) { const v = (data[i] - 128) / 128; sum += v * v; }
        const rms = Math.min(1, Math.sqrt(sum / data.length) * 3.2);
        setLevel((prev) => [...prev.slice(1), rms]);
      };
      frame.current = requestAnimationFrame(tick);
    } catch { /* no meter: the recording still works */ }

    const type = pickType();
    let r;
    try { r = type ? new window.MediaRecorder(media, { mimeType: type }) : new window.MediaRecorder(media); } catch {
      release(); setPhase('idle'); say('This browser could not start recording.'); return;
    }
    const chunks = [];
    discard.current = false;
    r.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunks.push(e.data); };
    r.onstop = async () => {
      release();
      if (recorder.current === r) recorder.current = null;
      setLevel(new Array(BARS).fill(0));
      if (discard.current || mine !== attempt.current) { setPhase('idle'); return; }
      const seconds = (Date.now() - began.current) / 1000;
      if (seconds < 0.6) { setPhase('idle'); setElapsed(0); say('That was too short - say what needs doing, then press Stop.'); return; }
      setPhase('converting');
      let wav;
      try {
        wav = await toWav16k(new Blob(chunks, { type: (r.mimeType || type || 'audio/webm').split(';')[0] }));
      } catch {
        if (mine === attempt.current) { setPhase('idle'); say('This browser could not read its own recording. Try again, or type it.'); }
        return;
      }
      if (mine !== attempt.current) return;
      setPhase('sending');
      try {
        const got = await cbs.current.send(wav.blob, lang.current);
        if (mine !== attempt.current) return;
        setPhase('idle');
        setElapsed(0);
        if (cbs.current.onText) cbs.current.onText(got);
      } catch (err) {
        if (mine !== attempt.current) return;
        setPhase('idle');
        setElapsed(0);
        say(err?.response?.data?.error || (err?.response ? 'That did not go through. Try again, or type it.'
          : 'No connection to the server. Try again, or type it.'));
      }
    };
    recorder.current = r;
    try { r.start(250); } catch { recorder.current = null; release(); setPhase('idle'); say('This browser could not start recording.'); return; }
    began.current = Date.now();
    setElapsed(0);
    setPhase('recording');
    timer.current = setInterval(() => {
      const s = (Date.now() - began.current) / 1000;
      setElapsed(Math.min(s, maxSeconds));
      if (s >= maxSeconds) stop();
    }, 200);
  }, [maxSeconds, release, stop, say]);

  // The page gone: let the microphone go.
  useEffect(() => () => {
    attempt.current += 1;
    discard.current = true;
    const r = recorder.current;
    if (r && r.state !== 'inactive') { try { r.stop(); } catch { /* stopped */ } }
    release();
  }, [release]);

  return { phase, elapsed, level, missing, maxSeconds, start, stop, cancel, busy: phase !== 'idle' };
}

/**
 * The mic. A press starts; held for more than half a second, letting go
 * stops, as a walkie-talkie does (and WhatsApp on a phone). The keyboard's
 * Enter or Space is a press.
 */
export function MicButton({ dictation, disabled = false, className = '', label = 'Dictate a to-do' }) {
  // The button goes as soon as the panel takes its place, so the letting go
  // is listened for on the window, once, and not tied to the button.
  const { start, stop, busy } = dictation;
  return (
    <button type="button" className={`tva-mic${className ? ` ${className}` : ''}`} disabled={disabled || busy}
            aria-label={label} title={`${label} - press, or hold and let go. ${PRIVACY}`}
            data-voice-mic=""
            onPointerDown={(e) => {
              if (e.button !== 0 || disabled || busy) return;
              e.preventDefault();
              const downAt = Date.now();
              const up = () => {
                window.removeEventListener('pointerup', up);
                window.removeEventListener('pointercancel', up);
                if (Date.now() - downAt > 500) stop();
              };
              window.addEventListener('pointerup', up);
              window.addEventListener('pointercancel', up);
              start();
            }}
            onClick={(e) => { if (e.detail === 0) start(); }}
            onContextMenu={(e) => e.preventDefault()}>
      <Icon name="mic" size={18} />
    </button>
  );
}

/** "EN ▾": the language Azure listens for. Remembered for next time. */
export function LanguageChip({ languages, value, onChange, disabled = false }) {
  const [open, setOpen] = useState(false);
  const box = useRef(null);
  const cur = languages.find((l) => l.code === value) || languages[0];
  useEffect(() => {
    if (!open) return undefined;
    const away = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener('pointerdown', away);
    return () => document.removeEventListener('pointerdown', away);
  }, [open]);
  const pick = (l) => { if (l.unavailable) return; onChange(l.code); rememberLanguage(l.code); setOpen(false); };
  return (
    <span className="tva-lang" ref={box}>
      <button type="button" className="tva-lang-chip" aria-haspopup="listbox" aria-expanded={open} disabled={disabled}
              aria-label={`Language: ${cur?.name || 'English'}. Change`} title="The language you are speaking"
              data-voice-lang={cur?.code}
              onClick={() => setOpen((v) => !v)}
              onKeyDown={(e) => { if (e.key === 'Escape' && open) { e.preventDefault(); e.stopPropagation(); setOpen(false); } }}>
        {cur?.short || cur?.code?.slice(0, 2).toUpperCase()}
        <Icon name="chevronDown" size={12} />
      </button>
      {open && (
        <ul className="tva-lang-menu" role="listbox" aria-label="Language">
          {languages.map((l) => (
            <li key={l.code} role="option" aria-selected={l.code === value}>
              <button type="button" className={l.code === value ? 'is-on' : ''} onClick={() => pick(l)}
                      disabled={Boolean(l.unavailable)} aria-disabled={l.unavailable ? 'true' : undefined}
                      title={l.unavailable || (l.needs_ai ? 'Heard as Malti and as English at once; Claude reads both' : undefined)}
                      onKeyDown={(e) => { if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); setOpen(false); } }}>
                <span className="tva-lang-short">{l.short || l.code.slice(0, 2).toUpperCase()}</span>{l.name}
                {l.code === value && <Icon name="check" size={13} />}
                {l.unavailable && <span className="tva-lang-hint" data-voice-lang-hint="">{l.unavailable}</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
    </span>
  );
}

/**
 * What quick add shows while the mic is on, and while the words come back.
 * Takes the keyboard while recording: Esc cancels, Space or Enter stops.
 * On the window, in the capture phase, so a dialog round it (chat's Make a
 * to-do) does not take that Escape for itself and close.
 */
export function VoicePanel({ dictation, languages, language, onLanguage, reading = false }) {
  const { phase, elapsed, level, maxSeconds, missing } = dictation;
  const live = phase === 'asking' || phase === 'recording';
  const stopRef = useRef(null);
  useEffect(() => {
    if (!live && !reading) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); dictation.cancel(); return; }
      if (phase !== 'recording') return;
      const t = e.target;
      // The language chip's own menu keeps its keys.
      if (t && t.closest && t.closest('.tva-lang')) return;
      if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); e.stopPropagation(); dictation.stop(); }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [live, reading, phase, dictation]);
  useEffect(() => { if (phase === 'recording' && stopRef.current) stopRef.current.focus({ preventScroll: true }); }, [phase]);

  const left = Math.max(0, Math.ceil(maxSeconds - elapsed));
  const working = phase === 'converting' || phase === 'sending' || reading;
  return (
    <div className={`tva-panel${working ? ' is-working' : ''}`} role="group" aria-label="Dictating a to-do" data-voice-phase={reading ? 'reading' : phase}>
      {working ? (
        <>
          <span className="tva-spin" aria-hidden="true" />
          <span className="tva-panel-words" role="status">
            {reading ? 'Reading what you said…' : 'Turning speech into text…'}
          </span>
          <button type="button" className="btn btn-outline tds-btn is-small" onClick={dictation.cancel}>Cancel</button>
        </>
      ) : (
        <>
          <span className={`tva-dot${phase === 'recording' ? ' is-on' : ''}`} aria-hidden="true" />
          <span className="tva-meter" aria-hidden="true">
            {level.map((v, i) => <span key={i} style={{ transform: `scaleY(${Math.max(0.08, v).toFixed(3)})` }} />)}
          </span>
          <span className="tva-time" role="timer" aria-label={`${clock(elapsed)} of ${clock(maxSeconds)}`}>
            {phase === 'asking' ? 'Starting…' : <>{clock(elapsed)}<span className="tva-of"> / {clock(maxSeconds)}</span></>}
          </span>
          <span className="tva-panel-words tva-hint">
            {phase === 'recording' && left <= 5 ? `Stops in ${left}s` : 'Listening - say what needs doing'}
          </span>
          <span className="tva-break" aria-hidden="true" />
          <LanguageChip languages={languages} value={language} onChange={onLanguage} />
          <button type="button" className="btn btn-outline tds-btn is-small tva-cancel" onClick={dictation.cancel}
                  title="Cancel (Esc)">Cancel</button>
          <button ref={stopRef} type="button" className="btn btn-primary tds-btn is-small tva-stop" disabled={phase !== 'recording'}
                  onClick={dictation.stop} title="Stop and read it (Space or Enter)">
            <Icon name="stop" size={12} />Stop
          </button>
          <span className="tva-progress" aria-hidden="true"><span style={{ transform: `scaleX(${Math.min(1, elapsed / maxSeconds).toFixed(3)})` }} /></span>
        </>
      )}
      {missing && live && <p className="tva-note">{missing} is not plugged in - using the system microphone.</p>}
    </div>
  );
}

/** The small "AI" mark on anything Claude proposed. */
export const AiBadge = ({ title = 'Suggested by AI (Claude) - check it before you keep it' }) => (
  <span className="tva-ai" title={title}><Icon name="sparkle" size={12} />AI</span>
);

// THE PREVIEW is VoiceDrafts.js (VoiceDraftList): every to-do the sentence makes.
export { VoiceDraftList } from './VoiceDrafts';
